import { Progress } from '@/components/ui/progress';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { motion } from 'framer-motion';

interface CreditSummaryCardProps {
  availableCredits: number | null;
  totalCredits: number | null;
  isLoading?: boolean;
  onRefresh?: () => void;
}

export function CreditSummaryCard({ 
  availableCredits, 
  totalCredits,
  isLoading = false,
  onRefresh
}: CreditSummaryCardProps) {
  return (
    <Card className="overflow-hidden border bg-gradient-to-br from-background via-muted/5 to-primary/5 transition-all hover:shadow-md">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-1.5 text-sm font-medium">
            <span className="h-2 w-2 rounded-full bg-primary"></span>
            크레딧 잔액
          </CardTitle>
          {onRefresh && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 rounded-full hover:bg-primary/10"
              onClick={onRefresh}
              disabled={isLoading}
            >
              <RefreshCw className="h-3 w-3" />
              <span className="sr-only">새로고침</span>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center space-y-2">
            <Loader2 className="mr-2 h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">크레딧 정보 로드 중...</span>
          </div>
        ) : (
          <>
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="text-3xl font-bold"
            >
              {availableCredits ?? 0}
            </motion.div>
            <p className="text-xs text-muted-foreground">
              {availableCredits !== null && totalCredits !== null ? 
                `총 ${totalCredits} 크레딧 중 ${availableCredits} 남음` : 
                '크레딧 정보 없음'}
            </p>
            {(availableCredits !== null && totalCredits !== null && totalCredits > 0) && (
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <Progress 
                  className="mt-2.5 h-1.5" 
                  value={(availableCredits / totalCredits) * 100} 
                />
              </motion.div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}